import { connect } from 'react-redux';

import {
  isExternalIncompleteUserSelector,
  isProfileIncompleteUserSelector,
  localeSelector,
  messagesSelector,
  personalDetailsInitialValuesSelector,
  registerCompanySelector,
  userDataSelector,
  userIdSelector
} from '../../helpers/selectors';

import { getCreationId, showApiError } from '../../helpers/components';
import {
  linkUserProfile,
  registerExternalUser,
  saveRegistrationStatusData,
  updateInformations
} from '../../actions/user';
import PersonalDetailsForm from '../account/PersonalDetails/PersonalDetailsForm';
import {
  formatAcceptTermsForApiCall,
  formatPersonalDetailsForApiCall
} from '../../helpers/formatters';
import {
  navigateToCreationDrivingLicense,
  navigateToRegistrationStatus
} from '../../utils/utils';
import { useCallback, useRef } from 'react';
import { getKeycloakEmail } from '../../utils/keycloak';
import { verifyRegisteredUser } from '../../actions/keycloak';
import { VERIFY_STATUS } from '../../constants/generic';

function CreationPersonalInfo(p) {
  const r = useRef({ fetching: false }).current;

  // Both post-KC registration flavours (external member creation and profile link)
  // decorate the payload the same way: the locale and the company the creation
  // route points at. No `login` in the body: /v2/members/keycloak is a JWT
  // contract, the server takes the e-mail from the token and the field is not
  // part of the schema.
  const formatDataForRegistration = useCallback(
    (payload) => {
      return {
        ...payload,
        locale: p.locale,
        companyId: getCreationId(p.routeParams)
      };
    },
    [p.locale, p.routeParams]
  );

  const updatePersonalInfo = useCallback(
    (info, terms) => {
      if (r.fetching) return;
      r.fetching = true;

      let registerAction;
      let payload;

      // A successful /me after this mutation is what replaces the external-user flag
      // with the readable member and his unfinished status. The mutation response is
      // not used for that handoff because it may describe a member /me cannot return.
      if (p.isExternalIncompleteUser) {
        registerAction = (id, data) => p.registerExternalUser(data);
        payload = formatPersonalDetailsForApiCall(p.userData, info);
        payload = formatDataForRegistration(payload);
      } else if (p.isProfileIncompleteUser) {
        registerAction = (id, data) => p.linkUserProfile(data);
        payload = formatPersonalDetailsForApiCall({}, info);
        payload = formatDataForRegistration(payload);
      } else {
        registerAction = p.updateInformations;
        payload = formatPersonalDetailsForApiCall(p.userData, info);
      }
      payload = formatAcceptTermsForApiCall(payload, terms, p.registerCompany);

      registerAction(p.userId, payload).then((data) => {
        if (
          ['user/REGISTER_SSO_SUCCESS', 'user/LINK_PROFILE_SUCCESS'].includes(data?.type)
        ) {
          p.verifyRegisteredUser().then((verifyStatus) => {
            r.fetching = false;
            if (verifyStatus === VERIFY_STATUS.MEMBER) {
              navigateToCreationDrivingLicense(getCreationId(p.routeParams));
            } else {
              // Every terminal non-member outcome uses the same route. Verification
              // outcomes without readable member data render a caution result there.
              p.saveRegistrationStatusData({
                email: getKeycloakEmail(),
                firstName: payload.firstName
              });
              navigateToRegistrationStatus();
            }
          });
        } else if (data?.type === 'user/UPDATE_INFO_SUCCESS') {
          r.fetching = false;
          navigateToCreationDrivingLicense(getCreationId(p.routeParams));
        } else {
          r.fetching = false;
          showApiError(p.m, data);
        }
      });
    },
    [p, r, formatDataForRegistration]
  );

  return (
    <PersonalDetailsForm
      onSubmit={updatePersonalInfo}
      initialValues={p.initialValues}
      isSubscribePage
      isCreationPage
    />
  );
}

const mapStateToProps = (state) => {
  return {
    initialValues: personalDetailsInitialValuesSelector(state),
    isExternalIncompleteUser: isExternalIncompleteUserSelector(state),
    isProfileIncompleteUser: isProfileIncompleteUserSelector(state),
    registerCompany: registerCompanySelector(state),
    m: messagesSelector(state),
    locale: localeSelector(state),
    userId: userIdSelector(state),
    userData: userDataSelector(state)
  };
};

const mapDispatchToProps = {
  updateInformations,
  registerExternalUser,
  saveRegistrationStatusData,
  linkUserProfile,
  verifyRegisteredUser
};

export default connect(mapStateToProps, mapDispatchToProps)(CreationPersonalInfo);

// TODO: when linking profile | preserve linking status
